import React, { useEffect, useRef, useState } from 'react';
import { X, CheckCircle, AlertCircle, Loader2, History, FileText, RotateCcw, ExternalLink, MessageSquare } from 'lucide-react';
import { MatchState, MatchAction } from '../../types';
import { formatDuration, formatRoundType } from '../../utils';
import { useResultSender } from '../../hooks';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { generateMatchEndAnnouncement } from '../../utils/announcementGenerator';
import { MATCH_END_ANNOUNCEMENT_FLAG } from '../../constants';
import { MatchHistoryModal } from './MatchHistoryModal';
import { RefereeSheetModal } from './RefereeSheetModal';
import { PostMatchFeedback } from './PostMatchFeedback';
import { PlayerAvatar, AnnouncementBanner } from '../atoms';
import * as matchStorage from '../../storage/matchStorage';
import { SendErrorBanner } from '../molecules';
import { generateRefereeSheet } from '../../utils';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useWakeLock } from '../../hooks/useWakeLock';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import { AppMode, useAppConfig } from '../../hooks/useAppConfig';
import { useResultScreenTimeout, formatCountdown } from '../../hooks/useResultScreenTimeout';
import { useTerminatedCallback } from '../../hooks/useTerminatedCallback';

interface AfterMatchProps {
  matchState: MatchState;
  dispatch: React.Dispatch<MatchAction>;
  wakeLock: ReturnType<typeof useWakeLock>;
  /** `paired`: kiosk tablet, the screen goes back to the waiting screen on its own (see below). */
  appMode?: AppMode;
}

/** Question asked on the way out of the result screen. */
type ExitDialog = 'notValidated' | 'feedback' | null;

/** Time left to window.close() before Close falls back to a new match (standalone). */
const CLOSE_FALLBACK_DELAY_MS = 300;

export function AfterMatch({ matchState, dispatch, wakeLock, appMode = 'standalone' }: AfterMatchProps) {
  const { t } = useTranslation();
  const { config } = useAppConfig();
  const paired = appMode === 'paired';

  // Annonce de fin de match rejouée ici (déplacée depuis DuringMatch) pour que
  // l'arbitre ait l'écran de validation sous les yeux au moment le plus attentif.
  const announcements = useAnnouncements();
  const hasPlayedMatchEndAnnouncement = useRef(false);

  const [showHistory, setShowHistory] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [exitDialog, setExitDialog] = useState<ExitDialog>(null);

  // ✅ NOUVEAUX ÉTATS pour le générateur de feuille
  const [isGeneratingSheet, setIsGeneratingSheet] = useState(false);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [generatedSheet, setGeneratedSheet] = useState<string | null>(null);
  const [showSheetModal, setShowSheetModal] = useState(false);

  // Wake Lock: s'assurer qu'il est désactivé après le match pour économiser la batterie
  useEffect(() => {
    if (wakeLock.isActive) {
      wakeLock.release();
      //console.log('[Wake Lock] Désactivé - match terminé');
    }
  }, [wakeLock]);
  
  const {
    sendStatus,
    lastError,
    clearSendError,
    sendAndCopyResults,
    copyResultsOnly,
    shouldSendResults,      // ← NOUVEAU
    shouldShowValidationUI, // ← NOUVEAU
    hasCallbackUrl
  } = useResultSender(matchState);

  // Automatic `terminated` callback, retried until it goes through: a paired tablet never leaves
  // this screen on its own before the result is recorded somewhere
  const { sent: terminatedSent, rejected: terminatedRejected } = useTerminatedCallback({
    matchState,
    enabled: shouldSendResults,
    stopped: sendStatus === 'success',
  });

  // Effacer toutes les données du match après validation du résultat
  const clearMatchSave = () => {
    matchStorage.clearAll();
  };

  const handleRollback = () => {
    // Vérifier qu'il y a un historique disponible
    if (matchState.history.length > 0) {
      // Utiliser l'action ROLLBACK_TO_DURING qui fait :
      // 1. ROLLBACK pour restaurer l'état précédent
      // 2. Changer currentScreen vers 'during'
      dispatch({ type: 'ROLLBACK_TO_DURING' });
    }
  };


  // Rejoue l'annonce de fin de match une seule fois, uniquement si on vient
  // d'une vraie transition de fin de match (drapeau posé par DuringMatch).
  // Un rechargement de page sur AfterMatch (recovery) ne pose pas ce drapeau,
  // donc l'annonce n'est pas rejouée intempestivement.
  useEffect(() => {
    if (hasPlayedMatchEndAnnouncement.current) return;
    if (!matchState.matchEnded || !matchState.winner) return;

    let pending = false;
    try {
      pending = sessionStorage.getItem(MATCH_END_ANNOUNCEMENT_FLAG) === '1';
      if (pending) sessionStorage.removeItem(MATCH_END_ANNOUNCEMENT_FLAG);
    } catch (e) {
      // sessionStorage indisponible : on ne rejoue pas l'annonce.
    }
    if (!pending) return;

    hasPlayedMatchEndAnnouncement.current = true;

    const winner = matchState.winner;
    const winnerName = winner === 'A'
      ? matchState.matchInfo.playerA
      : matchState.matchInfo.playerB;
    const lines = generateMatchEndAnnouncement({
      winnerName,
      winnerGames: matchState.setScores[winner],
      loserGames: matchState.setScores[winner === 'A' ? 'B' : 'A'],
      gameResults: matchState.gameResults,
      winner,
      t,
    });
    announcements.showAnnouncement('match_end', lines);
  }, [matchState, announcements, t]);

  
  // The self-assessment button is shown below Validate when the feature flag
  // is on and the match has a callback URL. It opens a modal questionnaire.
  const showFeedbackButton =
    FEATURE_FLAGS.postMatchFeedback && hasCallbackUrl;

  // Fonction pour gérer la validation avec effacement de la sauvegarde
  const handleValidation = async (): Promise<boolean> => {
    try {
      const validated = hasCallbackUrl ? await sendAndCopyResults() : await copyResultsOnly();

      // Effacer la sauvegarde après validation réussie (une validation refusée garde la
      // sauvegarde : un rechargement retrouve le match et l'arbitre peut réessayer)
      if (validated) clearMatchSave();
      return validated;

    } catch (error) {
      console.error('❌ Erreur lors de la validation:', error);
      return false;
    }
  };

  // --- Kiosk mode (paired tablet) ---
  // Back to the waiting screen (setup screen when standalone): same path as "New match"
  // (clears the match, reloads)
  const returnToIdle = () => dispatch({ type: 'START_NEW_MATCH' });

  // The result is safe once the automatic callback went through, or the referee validated,
  // or there is nobody to send it to
  const resultRecorded = !shouldSendResults || terminatedSent || sendStatus === 'success';
  const { remainingMs } = useResultScreenTimeout({
    enabled: paired && resultRecorded,
    timeoutMs: config.paired.resultScreenTimeoutSeconds * 1000,
    paused: sendStatus === 'sending' || feedbackOpen,
    onExpire: returnToIdle,
  });

  // Leaving the result screen: never validates on the referee's behalf, but asks before leaving
  // a result unvalidated, then offers the self-assessment once. On a kiosk tablet this is what
  // Close does; standalone, it is the fallback when the tab cannot be closed
  const leaveAfterFeedbackQuestion = () => {
    if (showFeedbackButton && !feedbackDone) {
      setExitDialog('feedback');
    } else {
      returnToIdle();
    }
  };
  const askBeforeLeaving = () => {
    if (shouldShowValidationUI && sendStatus !== 'success') {
      setExitDialog('notValidated');
    } else {
      leaveAfterFeedbackQuestion();
    }
  };
  const handleClose = () => {
    if (paired) {
      askBeforeLeaving();
      return;
    }
    // Standalone: close the tab when a script opened it (a link from the tournament system).
    // Browsers ignore window.close() on any other tab (QR code, typed URL, revoked tablet):
    // the button then leads to a new match instead of doing nothing
    window.close();
    setTimeout(() => {
      if (!window.closed) askBeforeLeaving();
    }, CLOSE_FALLBACK_DELAY_MS);
  };
  const handleValidateAndClose = async () => {
    setExitDialog(null);
    const validated = await handleValidation();
    if (validated) leaveAfterFeedbackQuestion();
  };
  const handleFeedbackClose = () => {
    setFeedbackOpen(false);
    setFeedbackDone(true);
    // The self-assessment is the referee's last gesture on a kiosk tablet
    if (paired) returnToIdle();
  };
  
  // Fonction pour obtenir le texte du bouton principal
  const getMainButtonConfig = () => {
    switch (sendStatus) {
      case 'sending':
        return {
          text: t('screens.after.validating'),
          icon: Loader2,
          className: 'bg-red-500 text-white cursor-not-allowed',
          disabled: true
        };
      case 'success':
        return {
          text: t('screens.after.validated'),
          icon: CheckCircle,
          className: 'bg-green-500 text-white',
          disabled: true
        };
      case 'error':
        return {
          text: t('screens.after.validationError'),
          icon: AlertCircle,
          className: 'bg-orange-500 text-white',
          disabled: false
        };
      default:
        // État non validé : bouton rendu très proéminent (pulse + ring + ombre)
        // pour inciter l'arbitre à valider et éviter qu'il oublie ce geste.
        return {
          text: t('screens.after.validate'),
          icon: CheckCircle,
          className: 'bg-red-500 text-white hover:bg-red-600 shadow-md shadow-red-500/40 ring-2 ring-red-300/60 animate-heartbeat',
          disabled: false
        };
    }
  };

  // ✅ FONCTION de génération de feuille d'arbitre
  const handleGenerateSheet = async () => {
    setIsGeneratingSheet(true);
    setSheetError(null);
    
    try {
      //console.log('🔄 Starting referee sheet generation...');
      const result = await generateRefereeSheet(matchState);
      
      if (result.success && result.html) {
        setGeneratedSheet(result.html);
        setShowSheetModal(true);
        //console.log('✅ Referee sheet generated successfully');
      } else {
        setSheetError(result.error || t('screens.after.generationFailed'));
        //console.error('❌ Sheet generation failed:', result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setSheetError(errorMessage);
      //console.error('❌ Exception during sheet generation:', error);
    } finally {
      setIsGeneratingSheet(false);
    }
  };
  
  const handleRedirect = () => {
    if (matchState.matchInfo.redirectUrl) {
      window.open(matchState.matchInfo.redirectUrl, '_blank');
    }
  };

  // Vérifier si on a une URL de redirection. Une tablette pairée l'ignore : elle n'a pas de page
  // MySquash où revenir, sa sortie est l'écran d'attente (le payload poussé n'en contient pas,
  // mais un redirectUrl envoyé quand même ouvrirait un onglet hors de la boucle du kiosque)
  const hasRedirectUrl = !paired && Boolean(matchState.matchInfo.redirectUrl);

  const buttonConfig = getMainButtonConfig();
  const ButtonIcon = buttonConfig.icon;

  // Reading order on this screen: one expected gesture at a time, one animation
  // at a time. While the result is not yet validated, Validate carries the
  // heartbeat nudge and self-assessment stays muted (still clickable, in case
  // validation fails or the referee wants to assess first). Once validation
  // succeeds, the nudge MOVES to self-assessment (it is expected per match) and
  // Validate settles into a calm "done" state. The two never pulse together.
  const feedbackNudgeActive = showFeedbackButton && sendStatus === 'success';
  const getFeedbackButtonClassName = () => {
    if (feedbackDone) {
      return 'bg-gray-300 text-gray-500 cursor-not-allowed';
    }
    if (feedbackNudgeActive) {
      // Validation done: self-assessment becomes the prominent, animated gesture.
      // Same heartbeat as Validate for a consistent "nudge" signature across the screen.
      return 'bg-amber-500 text-white hover:bg-amber-600 shadow-md shadow-amber-500/40 ring-2 ring-amber-300/50 animate-heartbeat';
    }
    // Before validation (or on error): muted outline, no animation, fully clickable.
    return 'bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100';
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-md landscape:max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white p-4 mb-4">
          {/* Ligne 1 : Competition, Category, Draw */}
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div className="font-semibold text-sm">{matchState.matchInfo.competitionName}</div>
            <div className="font-semibold text-sm">{matchState.matchInfo.categoryName}</div>
            <div className="font-semibold text-sm">{matchState.matchInfo.drawName}</div>
          </div>

          {/* Ligne 2 : Referee, Court, Round type, Scoring type */}
          <div className="grid grid-cols-4 gap-4 mb-3">
            <div className="text-gray-600 text-sm">{matchState.matchInfo.refereeName}</div>
            <div className="text-gray-600 text-sm">{matchState.matchInfo.courtName}</div>
            <div className="text-gray-600 text-sm">
              {formatRoundType(t, matchState.matchInfo.roundType)}
            </div>
            <div className="text-gray-600 text-sm">{matchState.matchInfo.scoringType}</div>
          </div>

          {/* Ligne 3 : Date et heure (alignée à droite) */}
          <div className="text-right">
            <div className="text-gray-500 text-xs">{matchState.matchInfo.dateTime}</div>
          </div>
        </div>
        
        {/* Match Summary */}
        <div className="bg-white p-4 mb-4">
          {/* Tableau des scores style scoreboard */}
          <div className="bg-white border border-gray-200 mb-4">
            {/* Joueur A */}
            <div className="flex items-center border-b border-gray-200">
              <div
                className="w-1 self-stretch"
                style={{ backgroundColor: matchState.playerAColor }}
              ></div>
              <div className="flex-1 px-3 py-2 text-sm font-medium text-gray-900 flex items-center gap-2">
                <PlayerAvatar
                  avatarUrl={matchState.matchInfo.playerAAvatar}
                  color={matchState.playerAColor}
                  size={28}
                  ringWidth={2}
                  alt={matchState.matchInfo.playerA}
                />
                <span>{matchState.matchInfo.playerA}</span>
                {matchState.winner === 'A' && <span>🏆</span>}
              </div>
              <div className="px-3 py-2 text-lg font-bold text-gray-700">
                {matchState.setScores.A}
              </div>
              {/* Scores des jeux terminés */}
              {matchState.gameResults.map((game, idx) => (
                <div
                  key={idx}
                  className="w-10 h-10 flex items-center justify-center text-sm font-medium text-white mx-0.5"
                  style={{ backgroundColor: game.winner === 'A' ? '#22c55e' : '#6b7280' }}
                >
                  {game.scoreA}
                </div>
              ))}
            </div>

            {/* Joueur B */}
            <div className="flex items-center">
              <div
                className="w-1 self-stretch"
                style={{ backgroundColor: matchState.playerBColor }}
              ></div>
              <div className="flex-1 px-3 py-2 text-sm font-medium text-gray-900 flex items-center gap-2">
                <PlayerAvatar
                  avatarUrl={matchState.matchInfo.playerBAvatar}
                  color={matchState.playerBColor}
                  size={28}
                  ringWidth={2}
                  alt={matchState.matchInfo.playerB}
                />
                <span>{matchState.matchInfo.playerB}</span>
                {matchState.winner === 'B' && <span>🏆</span>}
              </div>
              <div className="px-3 py-2 text-lg font-bold text-gray-700">
                {matchState.setScores.B}
              </div>
              {/* Scores des jeux terminés */}
              {matchState.gameResults.map((game, idx) => (
                <div
                  key={idx}
                  className="w-10 h-10 flex items-center justify-center text-sm font-medium text-white mx-0.5"
                  style={{ backgroundColor: game.winner === 'B' ? '#22c55e' : '#6b7280' }}
                >
                  {game.scoreB}
                </div>
              ))}
            </div>

            {/* Durées des jeux */}
            <div className="flex items-center border-t border-gray-200 bg-gray-50">
              <div className="w-1 self-stretch bg-transparent"></div>
              <div className="flex-1 px-3 py-1 text-xs text-gray-500">
                Total: {formatDuration(matchState.totalMatchDuration)}
              </div>
              <div className="px-3 py-1"></div>
              {matchState.gameResults.map((game, idx) => (
                <div
                  key={idx}
                  className="w-10 h-6 flex items-center justify-center text-xs text-gray-500 mx-0.5"
                >
                  {formatDuration(game.duration)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {/* Parcours principal : un geste attendu à la fois, une seule animation.
              Étape 1 = Valider (nudge), Étape 2 = Auto-évaluation (nudge après validation). */}
          {shouldShowValidationUI && (
            <button
              onClick={handleValidation}
              disabled={buttonConfig.disabled}
              className={`w-full py-3 font-semibold flex items-center justify-center gap-2 ${buttonConfig.className}`}
            >
              <ButtonIcon size={16} className={sendStatus === 'sending' ? 'animate-spin' : ''} />
              {buttonConfig.text}
            </button>
          )}
          {showFeedbackButton && (
            <button
              onClick={() => setFeedbackOpen(true)}
              disabled={feedbackDone}
              className={`w-full py-3 font-semibold flex items-center justify-center gap-2 ${getFeedbackButtonClassName()}`}
            >
              {feedbackDone ? <CheckCircle size={16} /> : <MessageSquare size={16} />}
              {feedbackDone ? t('screens.feedback.doneButton') : t('screens.feedback.openButton')}
            </button>
          )}

          {/* Actions secondaires (rarement utilisées) : style neutre uniforme, reculé
              sous un séparateur, pour ne pas concurrencer le parcours principal. */}
          <div className="pt-2 border-t border-gray-200">
            <div className="grid grid-cols-3 gap-2">
              {/* Bouton Rollback : désactivé si aucun historique ou match déjà validé */}
              <button
                onClick={handleRollback}
                disabled={matchState.history.length === 0 || sendStatus === 'success'}
                className={`py-2 px-2 font-medium flex flex-col items-center justify-center gap-1 text-xs ${
                  matchState.history.length === 0 || sendStatus === 'success'
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={
                  matchState.history.length === 0
                    ? t('screens.after.rollback.disabled')
                    : sendStatus === 'success'
                    ? 'Rollback disabled after validation'
                    : ''
                }
              >
                <RotateCcw size={14} />
                <span className="leading-tight">{t('screens.after.rollback.button')}</span>
              </button>

              {/* Bouton History */}
              <button
                onClick={() => setShowHistory(true)}
                className="py-2 px-2 font-medium text-gray-600 hover:bg-gray-100 flex flex-col items-center justify-center gap-1 text-xs"
              >
                <History size={14} />
                <span className="leading-tight">{t('screens.after.history')}</span>
              </button>

              {/* Bouton Referee sheet */}
              <button
                onClick={handleGenerateSheet}
                disabled={isGeneratingSheet}
                className={`py-2 px-2 font-medium flex flex-col items-center justify-center gap-1 text-xs ${
                  isGeneratingSheet
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {isGeneratingSheet ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <FileText size={14} />
                )}
                <span className="leading-tight">
                  {isGeneratingSheet ? t('screens.after.generating') : t('screens.after.refereeSheet')}
                </span>
              </button>
            </div>
          </div>

          {/* Tablette pairée : compte à rebours avant le retour à l'écran d'attente */}
          {paired && remainingMs !== null && (
            <p className="text-center text-xs text-gray-500" data-testid="kiosk-countdown">
              {t('screens.after.kiosk.returnIn', { params: { time: formatCountdown(remainingMs) } })}
            </p>
          )}

          {/* Sortie : neutre, en bas. Rediriger (retour tournoi) reste lég. accentué
              comme chemin de sortie normal ; Fermer est neutre. */}
          <div className={`grid gap-3 ${hasRedirectUrl ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {/* Bouton Redirect (conditionnel) */}
            {hasRedirectUrl && (
              <button
                onClick={handleRedirect}
                className="py-3 px-4 font-semibold bg-green-50 text-green-700 border border-green-300 hover:bg-green-100 flex items-center justify-center gap-2"
              >
                <ExternalLink size={16} />
                {t('screens.after.redirect')}
              </button>
            )}

            {/* Bouton Close */}
            <button
              onClick={handleClose}
              className="py-3 px-4 font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center gap-2"
            >
              <X size={16} />
              {t('screens.after.close')}
            </button>
          </div>
        </div>

        {/* Automatic result refused for good: nothing will retry it, the referee and the organizer
            must act (validate again, or close and report the result by hand) */}
        {shouldSendResults && terminatedRejected && sendStatus !== 'success' && (
          <SendErrorBanner
            error={terminatedRejected}
            messageKey="screens.after.autoSendRejected"
            autoDismissMs={0}
            onDismiss={() => {}}
            testId="terminated-rejected"
          />
        )}

        {/* Erreur d'envoi : message clair + détails techniques repliables (seulement si hasCallbackUrl) */}
        {shouldShowValidationUI && lastError && sendStatus === 'error' && (
          <SendErrorBanner error={lastError} onDismiss={clearSendError} />
        )}

        {/* ✅ NOUVEAU : Affichage des erreurs de génération */}
        {sheetError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200">
            <div className="text-red-800 text-sm font-medium">{t('screens.after.generationError')}</div>
            <div className="text-red-600 text-xs mt-1">{sheetError}</div>
          </div>
        )}
      </div>

      <MatchHistoryModal
        isOpen={showHistory}
        matchState={matchState}
        onClose={() => setShowHistory(false)}
      />

      {/* ✅ NOUVEAU : Modal feuille d'arbitre */}
      <RefereeSheetModal
        isOpen={showSheetModal}
        html={generatedSheet}
        onClose={() => {
          setShowSheetModal(false);
          setGeneratedSheet(null);
          setSheetError(null);
        }}
      />

      {feedbackOpen && (
        <PostMatchFeedback
          matchState={matchState}
          onClose={handleFeedbackClose}
        />
      )}

      {/* Questions posées en sortie de l'écran de résultat */}
      {exitDialog === 'notValidated' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="exit-not-validated">
          <div className="bg-white p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('screens.after.exit.notValidated.title')}</h3>
            <p className="text-gray-600 mb-6">{t('screens.after.exit.notValidated.message')}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleValidateAndClose}
                className="w-full py-3 font-semibold bg-red-500 text-white hover:bg-red-600"
              >
                {t('screens.after.exit.notValidated.validateAndClose')}
              </button>
              <button
                onClick={() => { setExitDialog(null); leaveAfterFeedbackQuestion(); }}
                className="w-full py-3 font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                {t('screens.after.exit.notValidated.closeWithout')}
              </button>
              <button
                onClick={() => setExitDialog(null)}
                className="w-full py-2 text-gray-500 hover:bg-gray-100"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {exitDialog === 'feedback' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="exit-feedback">
          <div className="bg-white p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('screens.after.exit.feedback.title')}</h3>
            <p className="text-gray-600 mb-6">{t('screens.after.exit.feedback.message')}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { setExitDialog(null); setFeedbackOpen(true); }}
                className="w-full py-3 font-semibold bg-amber-500 text-white hover:bg-amber-600"
              >
                {t('screens.after.exit.feedback.now')}
              </button>
              <button
                onClick={() => { setExitDialog(null); returnToIdle(); }}
                className="w-full py-3 font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                {t('screens.after.exit.feedback.skip')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Annonce de fin de match (rejouée sur cet écran, voir effet ci-dessus) */}
      <AnnouncementBanner
        announcement={announcements.currentAnnouncement}
        onDismiss={announcements.dismissAnnouncement}
      />

    </div>
  );
}